import { useEffect, useRef, useState } from 'react';
import { band } from '../lib/results';
import { prefersLessMotion } from '../lib/first-sight';

/**
 * An overall band that arrives, the first time a student sees it.
 *
 * It counts up in the half-bands a band moves in, slowing as it nears the
 * real one, and settles with a small give. Under a second; once only — the
 * page decides when it is fresh — and for someone who has asked for less
 * motion, simply the number.
 *
 * The moving digits are hidden from screen readers, which hear the band once,
 * as it is.
 */
export function RevealBand({ value, fresh, className }: { value: number | null; fresh: boolean; className: string }) {
  const animate = fresh && value !== null && !prefersLessMotion();
  const start = value === null ? 0 : Math.max(1, value - 3);
  const [shown, setShown] = useState<number | null>(animate ? start : value);
  const el = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    if (!animate || value === null) return undefined;
    const steps: number[] = [];
    for (let v = start; v <= value + 1e-9; v += 0.5) steps.push(Math.round(v * 2) / 2);
    if (steps[steps.length - 1] !== value) steps.push(value);
    // Each step waits a little longer than the last: the count slows into place.
    const total = 900;
    const weights = steps.map((_, i) => (i + 1) ** 1.6);
    const sum = weights.reduce((a, b) => a + b, 0);
    let at = 120;
    const timers = steps.map((v, i) => {
      at += (weights[i]! / sum) * total;
      return window.setTimeout(() => setShown(v), at);
    });
    const settle = window.setTimeout(() => {
      el.current?.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.06)' }, { transform: 'scale(1)' }],
        { duration: 320, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' },
      );
    }, at + 20);
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      window.clearTimeout(settle);
    };
  }, [animate, start, value]);

  return (
    <p ref={el} className={className} style={{ transformOrigin: 'left center' }}>
      <span aria-hidden="true">{band(shown)}</span>
      <span className="sr-only">{band(value)}</span>
    </p>
  );
}
